import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Radio,
  Checkbox,
  Space,
  Divider,
  Alert,
} from 'antd';
import { DesignTokens } from './DesignTokens';

const { TextArea } = Input;

export interface TenantFormData {
  id?: number;
  name: string;
  code: string;
  contact: string;
  phone: string;
  email?: string;
  userLimit: number;
  storageLimit: number;
  schemaType: 'shared' | 'independent';
  enabled: boolean;
}

export interface TenantFormProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues?: Partial<TenantFormData>;
  onOk?: (values: TenantFormData) => void;
  onCancel?: () => void;
  loading?: boolean;
}

export const TenantForm: React.FC<TenantFormProps> = ({
  open,
  mode,
  initialValues,
  onOk,
  onCancel,
  loading = false,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
      } else {
        form.resetFields();
      }
    }
  }, [open, initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onOk?.(values);
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  const isEditMode = mode === 'edit';

  return (
    <Modal
      data-testid="tenant-modal-form"
      title={isEditMode ? '编辑租户' : '新建租户'}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      width={640}
      okText={isEditMode ? '确定' : '创建'}
      cancelText="取消"
      styles={{
        header: {
          background: DesignTokens.colors.gray2,
          padding: `${DesignTokens.spacing.lg}px ${DesignTokens.spacing.xl}px`,
        },
        body: {
          padding: DesignTokens.spacing.xl,
        },
        footer: {
          background: DesignTokens.colors.gray2,
          borderTop: `1px solid ${DesignTokens.colors.gray4}`,
        },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          userLimit: 100,
          storageLimit: 50,
          schemaType: 'independent',
          enabled: true,
        }}
      >
        {/* 基本信息 */}
        <Divider orientation="left" style={{ 
          ...DesignTokens.typography.h3,
          borderColor: DesignTokens.colors.primary,
        }}>
          基本信息
        </Divider>

        <Form.Item
          label="租户名称"
          name="name"
          rules={[
            { required: true, message: '请输入租户名称' },
            { max: 50, message: '租户名称不能超过50个字符' },
          ]}
        >
          <Input data-testid="tenant-input-name" placeholder="请输入租户名称" />
        </Form.Item>

        <Form.Item
          label="租户代码"
          name="code"
          rules={[
            { required: true, message: '请输入租户代码' },
            { pattern: /^[A-Z0-9]+$/, message: '租户代码只能包含大写字母和数字' },
            { max: 20, message: '租户代码不能超过20个字符' },
          ]}
          extra={
            isEditMode ? (
              <Alert
                message="提示：代码创建后不可修改"
                type="warning"
                showIcon
                style={{ marginTop: 4 }}
              />
            ) : (
              <span style={{ color: DesignTokens.colors.warning }}>
                提示: 代码创建后不可修改
              </span>
            )
          }
        >
          <Input
            data-testid="tenant-input-code"
            placeholder="请输入租户代码(英文字母+数字)"
            disabled={isEditMode}
            style={isEditMode ? { background: DesignTokens.colors.gray2 } : undefined}
          />
        </Form.Item>

        <Space size="large" style={{ width: '100%' }}>
          <Form.Item
            label="联系人"
            name="contact"
            rules={[{ required: true, message: '请输入联系人' }]}
            style={{ flex: 1, marginBottom: 0 }}
          >
            <Input data-testid="tenant-input-contact" placeholder="请输入联系人" />
          </Form.Item>

          <Form.Item
            label="联系电话"
            name="phone"
            rules={[
              { required: true, message: '请输入联系电话' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码' },
            ]}
            style={{ flex: 1, marginBottom: 0 }}
          >
            <Input data-testid="tenant-input-phone" placeholder="请输入联系电话" />
          </Form.Item>
        </Space>

        <Form.Item
          label="联系邮箱"
          name="email"
          rules={[
            { type: 'email', message: '请输入正确的邮箱地址' },
          ]}
          style={{ marginTop: DesignTokens.spacing.lg }}
        >
          <Input data-testid="tenant-input-email" placeholder="example@company.com" />
        </Form.Item>

        {/* 容量配置 */}
        <Divider orientation="left" style={{ 
          ...DesignTokens.typography.h3,
          borderColor: DesignTokens.colors.primary,
        }}>
          容量配置
        </Divider>

        <Space size="large" style={{ width: '100%' }}>
          <Form.Item
            label="用户数上限"
            name="userLimit"
            rules={[{ required: true, message: '请输入用户数上限' }]}
            style={{ flex: 1, marginBottom: 0 }}
          >
            <InputNumber
              data-testid="tenant-input-user-limit"
              min={1}
              max={10000}
              addonAfter="人"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            label="存储空间上限"
            name="storageLimit"
            rules={[{ required: true, message: '请输入存储空间上限' }]}
            style={{ flex: 1, marginBottom: 0 }}
          >
            <InputNumber
              data-testid="tenant-input-storage-limit"
              min={1}
              max={10000}
              addonAfter="GB"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Space>

        <Form.Item
          label="Schema配置"
          name="schemaType"
          rules={[{ required: true, message: '请选择Schema配置' }]}
          extra={
            isEditMode ? (
              <span style={{ color: DesignTokens.colors.gray7 }}>
                当前配置: {form.getFieldValue('schemaType') === 'independent' ? '独立Schema' : '共享Schema'}
              </span>
            ) : (
              <span style={{ color: DesignTokens.colors.gray7 }}>
                说明: 独立Schema提供更好的数据隔离
              </span>
            )
          }
          style={{ marginTop: DesignTokens.spacing.lg }}
        >
          <Radio.Group data-testid="tenant-radio-schema-type" disabled={isEditMode}>
            <Radio value="shared">共享Schema</Radio>
            <Radio value="independent">独立Schema</Radio>
          </Radio.Group>
        </Form.Item>

        {/* 状态设置 */}
        <Divider orientation="left" style={{ 
          ...DesignTokens.typography.h3,
          borderColor: DesignTokens.colors.primary,
        }}>
          状态设置
        </Divider>

        <Form.Item
          label="初始状态"
          name="enabled"
          valuePropName="checked"
        >
          <Checkbox data-testid="tenant-checkbox-enabled">启用</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};
